import { Tab } from '@headlessui/react';
import { useJsApiLoader } from '@react-google-maps/api';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import AnimatedTaskModal from 'components/common/animated-task-modal';
import { ContextMenuProvider } from 'components/common/context-menu-manager';
import GlobalContextMenu from 'components/common/global-context-menu';
import BreadcrumbBar from 'components/layout/breadcrumb-bar';
import ChatBot from 'components/layout/chat-bot';
import DebugMenu from 'components/layout/debug-menu';
import Footer from 'components/layout/footer';
import Header from 'components/layout/header';
import LoadingOverlay from 'components/layout/loading-overlay';
import SettingsPanel from 'components/layout/settings-panel';
import TipsDimOverlay from 'components/layout/tips-dim-overlay';
import TipsPanel from 'components/layout/tips-panel';
import ProjectNotes from 'components/project-page/project-notes';
import PropertySummary from 'components/project-page/property-summary';
import AccessoriesHydrationWatcher from 'components/watchers/accessories-hydration-watcher';
import AutoSaveWatcher from 'components/watchers/autosave-watcher';
import BTUWatcher from 'components/watchers/btu-watcher';
import CompletedStepsWatcher from 'components/watchers/completed-steps-watcher';
import ConcealedDuctRevertWatcher from 'components/watchers/concealed-duct-revert-watcher';
import DuctedDesignConfigurationWatcher from 'components/watchers/ducted-design-configuration-watcher';
import DuctedNewSystemSlotsWatcher from 'components/watchers/ducted-new-system-slots-watcher';
import ErrorLogReproduceWatcher from 'components/watchers/error-log-reproduce-watcher';
import HybridWholeHouseConfigurationWatcher from 'components/watchers/hybrid-whole-house-configuration-watcher';
import MatchingAirHandlersWatcher from 'components/watchers/matching-air-handlers-watcher';
import PageProgressWatcher from 'components/watchers/page-progress-watcher';
import PtacConfigurationWatcher from 'components/watchers/ptac-configuration-watcher';
import QuotedSystemsHydrationWatcher from 'components/watchers/quoted-systems-hydration-watcher';
import ResetDataWatcher from 'components/watchers/reset-data-watcher';
import SSEWatcher from 'components/watchers/sse-watcher';
import StructureAttachmentsRefreshWatcher from 'components/watchers/structure-attachments-refresh-watcher';
import TabLockWatcher from 'components/watchers/tab-lock-watcher';
import TonnageFilterSeedWatcher from 'components/watchers/tonnage-filter-seed-watcher';
import ValidSystemsWatcher from 'components/watchers/valid-systems-watcher';
import { FooterProvider } from 'contexts/footer-context';
import { GOOGLE_MAP_LIBRARIES } from 'data/constants';
import { ActiveTabType } from 'data/types';
import { fetchDefaultData } from 'features/data';
import {
  isReproducingErrorLog,
  setActiveItem,
  setActiveTab,
  setGoogleApiLoaded,
  setShowPropertySummary
} from 'features/ui';
import AccessoriesPage from 'pages/accessories';
import ChooseSystemsPage from 'pages/choose-systems';
import ConfigurationsPage from 'pages/configurations';
import EquipmentPreferencesPage from 'pages/equipment-preferences';
import ExistingEquipmentPage from 'pages/existing-equipment';
import ProjectPage from 'pages/project';
import QuotePage from 'pages/quote';
import StructurePage from 'pages/structure';
import useEquipmentPreferencesPageVisible from 'hooks/use-equipment-preferences-page-visible';
import useExistingEquipmentPageVisible from 'hooks/use-existing-equipment-page-visible';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast, Toaster, useToasterStore } from 'react-hot-toast';
import { HighlightShape } from 'types/highlight';
import { initializeApp } from 'utils/data';
import { getReproduceErrorLogId } from 'utils/error-log/reproduce';
import { getQueryParam, removeQueryParam } from 'utils/helpers';
import { getHighlightShape } from 'utils/highlight';
import { findSpaceById } from 'utils/structure';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import { createActiveItem, errorModal, warningModal } from 'utils/ui';
import { getVisibleTabIndex, getVisibleTabs } from 'utils/visible-tabs';

const TAB_PANELS: Record<
  ActiveTabType,
  { className: string; page: JSX.Element }
> = {
  project: {
    className: 'h-full p-5 focus:outline-none',
    page: <ProjectPage />
  },
  existingEquipment: {
    className: 'p-5 focus:outline-none',
    page: <ExistingEquipmentPage />
  },
  equipmentPreferences: {
    className: 'p-5 focus:outline-none',
    page: <EquipmentPreferencesPage />
  },
  structure: {
    className: 'h-full p-5 focus:outline-none',
    page: <StructurePage />
  },
  configurations: {
    className: 'h-full p-5 focus:outline-none',
    page: <ConfigurationsPage />
  },
  chooseSystems: {
    className: 'h-full p-5 focus:outline-none',
    page: <ChooseSystemsPage />
  },
  accessories: {
    className: 'p-5 focus:outline-none',
    page: <AccessoriesPage />
  },
  quote: {
    className: 'p-5 focus:outline-none',
    page: <QuotePage />
  }
};

export default function IndexPage() {
  const dispatch = useAppDispatch();
  const {
    items,
    attributes,
    validSizingAttributes,
    criticalDataError,
    nonCriticalDataError,
    airHandlerProducts,
    condenserProducts,
    BTULimits
  } = useAppSelector((state) => state.data);
  const { attomData } = useAppSelector((state) => state.project);
  const { structure } = useAppSelector((state) => state.structure);
  const {
    activeTab,
    showPropertySummary,
    showChatPanel,
    debugMode,
    reproducingErrorLogId,
    activeTipSequence
  } = useAppSelector((state) => state.ui);
  const isReproducing = isReproducingErrorLog(
    reproducingErrorLogId,
    getReproduceErrorLogId()
  );
  const { toasts } = useToasterStore();
  const [loading, setLoading] = useState(true);
  const [projectDataLoaded, setProjectDataLoaded] = useState(false);
  const spaceId = getQueryParam('spaceId');
  const showExistingEquipmentTab = useExistingEquipmentPageVisible();
  const showEquipmentPreferencesTab = useEquipmentPreferencesPageVisible();
  const bypassMode = useSystemDesignBypassMode();
  const systemDesignLocked = isSystemDesignLocked(bypassMode);
  const visibleTabs = useMemo(
    () =>
      getVisibleTabs({
        showExistingEquipmentTab,
        showEquipmentPreferencesTab,
        systemDesignLocked
      }),
    [showExistingEquipmentTab, showEquipmentPreferencesTab, systemDesignLocked]
  );
  const activeTabIndex = getVisibleTabIndex(visibleTabs, activeTab);

  const { isLoaded: isGoogleApiLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: GOOGLE_MAP_LIBRARIES
  });

  useEffect(() => {
    dispatch(setGoogleApiLoaded(isGoogleApiLoaded));
  }, [isGoogleApiLoaded]);

  useEffect(() => {
    dispatch(fetchDefaultData());
  }, [dispatch]);

  useEffect(() => {
    const initialize = async () => {
      const { success } = await initializeApp();

      if (success) {
        setProjectDataLoaded(true);
      }

      setTimeout(() => {
        setLoading(false);
      }, 500);
    };

    const hasValidSizingAttributes = Object.values(validSizingAttributes).some(
      (arr) => arr.length > 0
    );

    if (
      items.length &&
      attributes.length &&
      hasValidSizingAttributes &&
      loading
    ) {
      initialize();
    }
  }, [items, attributes, validSizingAttributes]);

  const warnOnLeave = (e: BeforeUnloadEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (!projectDataLoaded) {
      window.addEventListener('beforeunload', warnOnLeave);
    }
    return () => {
      window.removeEventListener('beforeunload', warnOnLeave);
    };
  }, [projectDataLoaded]);

  useEffect(() => {
    dispatch(setShowPropertySummary(false));
  }, [activeTab]);

  useEffect(() => {
    const toastLimit = 3;
    const uniqueMessages = new Set();
    const toastsToShow = toasts
      .filter((t) => t.visible)
      .filter((t) => {
        const isUnique = !uniqueMessages.has(t.message);
        uniqueMessages.add(t.message);
        return isUnique;
      })
      .slice(-toastLimit);

    toasts
      .filter((t) => !toastsToShow.find((showToast) => showToast.id === t.id))
      .forEach((t) => toast.dismiss(t.id));
  }, [JSON.stringify(toasts)]);

  useEffect(() => {
    if (criticalDataError) {
      errorModal(
        'Critical data could not be fetched, please try again later.',
        {
          showConfirmButton: false,
          allowEscapeKey: false,
          allowOutsideClick: false
        }
      );
    }

    if (nonCriticalDataError) {
      warningModal(
        'Some non-critical data may not be available, functionality may be affected.'
      );
    }
  }, [criticalDataError, nonCriticalDataError]);

  useEffect(() => {
    if (!loading && spaceId && structure.length > 0) {
      const space = findSpaceById(structure, spaceId);

      if (space) {
        dispatch(setActiveTab('structure'));
        dispatch(
          setActiveItem(createActiveItem({ type: 'space', item: space }))
        );
        removeQueryParam('spaceId');
      } else {
        console.error(`Space ${spaceId} not found`);
        errorModal(`Space ${spaceId} not found`);
      }
    }
  }, [spaceId, structure, loading]);

  const [tipShapes, setTipShapes] = useState<HighlightShape[]>([]);
  const [tipTargetsUnresolved, setTipTargetsUnresolved] = useState(false);
  const rafRef = useRef<number | null>(null);
  const targetResizeObserverRef = useRef<ResizeObserver | null>(null);
  const observedTargetsRef = useRef<Element[]>([]);

  const updateTipShapes = useCallback(() => {
    const targets =
      activeTipSequence?.tips[activeTipSequence.tipIndex]?.targets;
    const showTips = !!targets && targets.length > 0;

    const elements: Element[] = [];
    const data: HighlightShape[] = [];
    if (showTips) {
      targets.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          elements.push(element);
          data.push(getHighlightShape(element));
        }
      });
    }

    const observer = targetResizeObserverRef.current;
    const previous = observedTargetsRef.current;
    const observedTargetsChanged =
      elements.length !== previous.length ||
      elements.some((element, index) => element !== previous[index]);
    if (observer && observedTargetsChanged) {
      observer.disconnect();
      elements.forEach((element) => observer.observe(element));
      observedTargetsRef.current = elements;
    }

    setTipShapes(data);
    setTipTargetsUnresolved(!!activeTipSequence && data.length === 0);
  }, [activeTipSequence]);

  useEffect(() => {
    const handleUpdate = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateTipShapes);
    };

    targetResizeObserverRef.current = new ResizeObserver(handleUpdate);
    observedTargetsRef.current = [];

    updateTipShapes();

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    const observer = new MutationObserver(handleUpdate);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      observer.disconnect();
      targetResizeObserverRef.current?.disconnect();
      targetResizeObserverRef.current = null;
      observedTargetsRef.current = [];
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateTipShapes]);

  const activeTipIndex = activeTipSequence?.tipIndex ?? null;
  const activeTipTargets =
    activeTipSequence?.tips[activeTipSequence.tipIndex]?.targets;
  const activeTipTargetsKey = activeTipTargets?.join('|') ?? null;

  useEffect(() => {
    if (!activeTipTargets || activeTipTargets.length === 0) {
      return;
    }

    const anchorElement = activeTipTargets
      .map((id) => document.getElementById(id))
      .find((element) => element !== null);
    anchorElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }, [activeTipIndex, activeTipTargetsKey]);

  return (
    <FooterProvider>
      <ContextMenuProvider>
        <main className='flex h-[100vh] w-full flex-col bg-gray-100 text-gray-900'>
          <Tab.Group selectedIndex={activeTabIndex}>
            {debugMode && (
              <div className='flex w-full justify-center bg-red-300 py-2 font-semibold'>
                Debug mode, auto-save disabled!
                {!projectDataLoaded && ' (Project data not loaded)'}
              </div>
            )}
            <ErrorLogReproduceWatcher loading={loading} />
            {!projectDataLoaded && !loading && (
              <div className='flex w-full justify-center bg-orange-300 py-2 font-semibold'>
                Project data not loaded, auto-save disabled!
              </div>
            )}
            <div className='flex w-full grow flex-col'>
              <BreadcrumbBar />
              <Header />
              <div className='relative flex grow'>
                {activeTab !== 'quote' && <SettingsPanel loading={loading} />}
                <div className='relative grow'>
                  <div className='absolute inset-0 overflow-y-auto overflow-x-hidden'>
                    {showPropertySummary && attomData && !criticalDataError && (
                      <div className='p-5'>
                        <PropertySummary />
                      </div>
                    )}
                    {!showPropertySummary && !criticalDataError && (
                      <Tab.Panels className='h-full focus:outline-none'>
                        {visibleTabs.map((tab) => (
                          <Tab.Panel
                            key={tab}
                            className={TAB_PANELS[tab].className}
                          >
                            {TAB_PANELS[tab].page}
                          </Tab.Panel>
                        ))}
                      </Tab.Panels>
                    )}
                    {criticalDataError && (
                      <div className='flex h-full w-full items-center justify-center'>
                        <span>
                          Critical data error. Please reload the tool.
                        </span>
                      </div>
                    )}
                  </div>
                  <LoadingOverlay
                    loading={
                      loading ||
                      (activeTab === 'configurations' &&
                        (!BTULimits.length ||
                          !airHandlerProducts.length ||
                          !condenserProducts.length))
                    }
                  />
                </div>
                <ChatBot />
                <ProjectNotes />
              </div>
            </div>
          </Tab.Group>
          <Footer />
          <div
            className={`fixed bottom-28 z-20 flex flex-col gap-5 transition-all duration-200 ${
              showChatPanel ? 'right-[420px]' : 'right-5'
            }`}
          />
          {/* Reproduce mode (including a failed snapshot load): keep
              mutating watchers unmounted so hydrate — or a first mount
              after load — is not treated as a user edit. */}
          {!isReproducing && (
            <>
              <PageProgressWatcher />
              <CompletedStepsWatcher />
              <ResetDataWatcher loading={loading} />
              {projectDataLoaded && (
                <>
                  <HybridWholeHouseConfigurationWatcher loading={loading} />
                  <DuctedDesignConfigurationWatcher loading={loading} />
                  <DuctedNewSystemSlotsWatcher loading={loading} />
                  <PtacConfigurationWatcher loading={loading} />
                </>
              )}
              <TabLockWatcher />
              <BTUWatcher />
              <ValidSystemsWatcher loading={loading} />
              <ConcealedDuctRevertWatcher />
              <MatchingAirHandlersWatcher />
              <AccessoriesHydrationWatcher />
              <QuotedSystemsHydrationWatcher />
            </>
          )}
          <Toaster
            position='top-right'
            containerClassName={
              debugMode || isReproducing || !projectDataLoaded
                ? 'mt-[90px]'
                : 'mt-[50px]'
            }
            toastOptions={{
              duration: 2000,
              className: '[overflow-wrap:anywhere]'
            }}
          />
          {!debugMode && !isReproducing && projectDataLoaded && (
            <AutoSaveWatcher loading={loading} />
          )}
          {projectDataLoaded && !isReproducing && <SSEWatcher />}
          {projectDataLoaded && <StructureAttachmentsRefreshWatcher />}
          <TonnageFilterSeedWatcher />
          <DebugMenu />
          <GlobalContextMenu />
          <AnimatedTaskModal />
          <TipsDimOverlay shapes={tipShapes} />
          {activeTipSequence &&
            (tipShapes.length > 0 || tipTargetsUnresolved) && (
              <TipsPanel
                sequence={activeTipSequence}
                anchorRect={tipShapes[0]?.rect ?? null}
              />
            )}
        </main>
      </ContextMenuProvider>
    </FooterProvider>
  );
}
